// src/context/AccountProvider.tsx

import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Socket } from "socket.io-client";
import { CACHE_KEYS, CACHE_OPTIONS } from "../constants/cache-keys";
import {
  Account,
  AccountContextType,
  AccountProviderProps,
  AccountState,
} from "../interfaces/all";
import { getCompanyData } from "../services/api";
import authService from "../services/auth";
import socketService, { initialSocketState } from "../services/socket";
import { CacheManager } from "../utils/cache-manager";
import { ErrorHandler } from "../utils/error-handler";
import { useToast } from "./ToastContext";
import { useChatStore } from "../store/chatStore";

const SESSION_CONFIG = {
  CHECK_INTERVAL: 5 * 60 * 1000, // 5 minutes
  CHECK_DEBOUNCE: 1000, // 1 second
  LOCK_TIMEOUT: 60000, // 60 seconds
  TOKEN_REFRESH_INTERVAL: 10 * 60 * 1000, // 10 minutes - refresh token antes de expirar
  TOKEN_EXPIRY_BUFFER: 2 * 60 * 1000, // 2 minutes - buffer antes da expiração
} as const;

const initialAccountState: AccountState = {
  account: undefined,
  socketState: initialSocketState,
  isLoading: false,
  error: null,
  authState: "logout",
  hasAccount: false,
  session: {
    lastCheck: 0,
    lockExpiration: 0,
  },
};

// Exportar o contexto
export const AccountContext = createContext<AccountContextType>({
  account: undefined,
  setAccount: (() => {}) as Dispatch<SetStateAction<Account | undefined>>,
  login: async () => {},
  logout: async () => {},
  socket: { current: null },
  socketState: initialSocketState,
  isLoading: false,
  error: null,
  authState: "logout",
});

export const AccountProvider: React.FC<AccountProviderProps> = ({
  children,
}) => {
  const [state, setState] = useState<AccountState>({
    ...initialAccountState,
    isLoading: true, // Inicializa como true
  });
  const socket = useRef<Socket | null>(null);
  const isInitialCheckDone = useRef(false);
  const hasShownExpiredMessage = useRef(false);
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tokenRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const { showError, showToast } = useToast();
  const socketStateRef = useRef(state.socketState);

  // Atualiza a ref quando o estado muda
  useEffect(() => {
    socketStateRef.current = state.socketState;
  }, [state.socketState]);

  // Efeito para configurar os callbacks
  useEffect(() => {
    authService.setErrorCallback((error) => {
      showError(error);
    });

    socketService.setErrorCallback((error) => {
      showError(error);
    });

    socketService.setStateCallback((updates) => {
      // Usa a ref para evitar atualizações desnecessárias
      if (
        JSON.stringify(socketStateRef.current) !==
        JSON.stringify({ ...socketStateRef.current, ...updates })
      ) {
        setState((prev) => ({
          ...prev,
          socketState: { ...prev.socketState, ...updates },
        }));
      }
    });

    return () => {
      authService.setErrorCallback(() => {});
      socketService.setErrorCallback(() => {});
      socketService.setStateCallback(() => {});
    };
  }, [showError]); // Adicionada dependência showError

  // Efeito para salvar estado da sessão no cache
  useEffect(() => {
    CacheManager.set(
      CACHE_KEYS.SESSION.ACCOUNT,
      state.session,
      CACHE_OPTIONS.SESSION.ACCOUNT,
    );
  }, [state.session]);

  const updateState = useCallback((updates: Partial<AccountState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleSessionError = useCallback(
    (error: unknown) => {
      const isAuthError =
        error &&
        typeof error === "object" &&
        "type" in error &&
        (error as { type: string }).type === "authentication";

      updateState({
        hasAccount: false,
        authState: isAuthError ? "expired" : "logout",
      });

      // Limpar timeouts de refresh
      if (tokenRefreshTimeoutRef.current) {
        clearTimeout(tokenRefreshTimeoutRef.current);
        tokenRefreshTimeoutRef.current = null;
      }

      if (isAuthError && !hasShownExpiredMessage.current) {
        hasShownExpiredMessage.current = true;
        const errorResponse = ErrorHandler.handleApiError(error);
        showError(errorResponse.userMessage);

        // Limpar timeout anterior se existir
        if (redirectTimeoutRef.current) {
          clearTimeout(redirectTimeoutRef.current);
        }

        // Recarrega a página após 2 segundos para garantir que a mensagem seja vista
        redirectTimeoutRef.current = setTimeout(() => {
          window.location.href = "/";
        }, 2000);
      }
    },
    [updateState, showError],
  );

  // Função para agendar refresh de token
  const scheduleTokenRefresh = useCallback(() => {
    // Limpar timeout anterior se existir
    if (tokenRefreshTimeoutRef.current) {
      clearTimeout(tokenRefreshTimeoutRef.current);
    }

    // Agendar próximo refresh
    tokenRefreshTimeoutRef.current = setTimeout(async () => {
      try {
        await authService.refreshToken();
        showToast({
          type: "success",
          message: "Token renovado automaticamente",
        });

        // Agendar próximo refresh
        scheduleTokenRefresh();
      } catch (error) {
        // Se falhar no refresh, tratar como erro de sessão
        handleSessionError(error);
      }
    }, SESSION_CONFIG.TOKEN_REFRESH_INTERVAL);
  }, [showToast, handleSessionError]);

  const handleLogout = useCallback(async () => {
    try {
      updateState({ isLoading: true, error: null });

      // Limpar timeouts
      if (tokenRefreshTimeoutRef.current) {
        clearTimeout(tokenRefreshTimeoutRef.current);
        tokenRefreshTimeoutRef.current = null;
      }

      // Limpar event handlers e desconectar socket
      socketService.removeEventHandlers();
      socketService.disconnect();
      socket.current = null;

      await authService.logout();

      updateState({
        ...initialAccountState,
        authState: "logout",
      });
    } catch (error) {
      updateState({
        ...initialAccountState,
        authState: "logout",
        error: error instanceof Error ? error.message : "Erro ao fazer logout",
      });
    } finally {
      updateState({ isLoading: false });
    }
  }, [updateState]);

  const initializeSocket = useCallback(
    (accountData: Account) => {
      if (socket.current?.connected) return;

      const socketUrl = import.meta.env.VITE_BACKEND_URL;

      // Log de debug para variável de ambiente

      if (!socketUrl) {
        console.error("URL do socket não definida");
        updateState({
          socketState: {
            ...socketStateRef.current,
            isConnecting: false,
            lastError: "URL do socket não configurada",
          },
        });
        return;
      }

      // Conectar com autenticação e sala da empresa
      socket.current = socketService.connect(
        socketUrl,
        accountData.id,
        accountData.company_id,
      );

      // Configurar handlers de eventos do socket
      socketService.setEventHandlers({
        onNewMessage: (_data) => {},
        onMessageSending: (_data) => {}, // eslint-disable-line @typescript-eslint/no-unused-vars
        onMessageError: (data) => {
          showError(new Error(data.error));
        },
        onMessageStatusUpdate: undefined, // Remover handler global para permitir handlers locais
        onAIStateUpdate: (_data) => {}, // eslint-disable-line @typescript-eslint/no-unused-vars
        onMessageReadStatusUpdate: (_data) => {}, // eslint-disable-line @typescript-eslint/no-unused-vars
        onAllMessagesRead: (_data) => {}, // eslint-disable-line @typescript-eslint/no-unused-vars
        onServerConnectionStatus: (data) => {
          if (data.status === "disconnected") {
            showToast({
              type: "warning",
              message: "Server desconectado. Tentando reconectar...",
            });
          } else if (data.status === "connected") {
            showToast({
              type: "success",
              message: "Server conectado com sucesso!",
            });
          }
        },
        onPong: () => {},
        onTyping: (data) => {
          useChatStore.getState().setTyping(data.phoneNumber, data.isTyping);
        },
      });
    },
    [updateState, showError, showToast],
  );

  const handleSetAccount: Dispatch<SetStateAction<Account | undefined>> =
    useCallback(
      async (newAccount) => {
        const processAccount = async (account: Account | undefined) => {
          if (!account) return account;

          try {
            const companyData = await getCompanyData(account.company_id);
            if (companyData.status === "success" && companyData.data) {
              account.company =
                companyData.data as unknown as Account["company"];
            } else if (state.account?.company) {
              account.company = state.account.company;
            }
          } catch (error) {
            if (state.account?.company) {
              account.company = state.account.company;
            }
          }
          return account;
        };

        if (typeof newAccount === "function") {
          const updatedAccount = await processAccount(
            newAccount(state.account),
          );
          setState((prev) => ({
            ...prev,
            account: updatedAccount,
            error: null,
          }));
        } else {
          const processedAccount = await processAccount(newAccount);
          setState((prev) => ({
            ...prev,
            account: processedAccount,
            error: null,
          }));
          if (processedAccount && !socket.current?.connected) {
            initializeSocket(processedAccount);
          }
        }
      },
      [initializeSocket, state.account],
    );

  const verifySession = useCallback(async () => {
    try {
      const account = await authService.initialize();
      updateState({
        account,
        hasAccount: true,
        authState: "login",
        session: {
          lastCheck: Date.now(),
          lockExpiration: Date.now() + SESSION_CONFIG.LOCK_TIMEOUT,
        },
      });

      // Iniciar refresh automático de token
      scheduleTokenRefresh();

      return true;
    } catch (error) {
      handleSessionError(error);
      return false;
    }
  }, [updateState, handleSessionError, scheduleTokenRefresh]);

  // Efeito para verificação inicial de sessão
  useEffect(() => {
    const checkInitialSession = async () => {
      // Se já foi feito o check inicial, não executa novamente
      if (isInitialCheckDone.current) {
        return;
      }

      if (window.location.pathname === "/sso-login") {
        updateState({ isLoading: false });
        return;
      }

      // Marca como feito antes de qualquer operação assíncrona
      isInitialCheckDone.current = true;

      const currentAuthState = authService.getAuthState();

      if (currentAuthState !== "login") {
        updateState({
          authState: currentAuthState,
          isLoading: false,
        });
        return;
      }

      try {
        await verifySession();
      } finally {
        updateState({ isLoading: false });
      }
    };

    checkInitialSession();
  }, [updateState, verifySession]); // Adicionadas dependências necessárias

  // Efeito para verificação periódica de sessão
  useEffect(() => {
    let sessionCheckTimeout: ReturnType<typeof setInterval> | undefined;

    if (state.authState === "login" && state.hasAccount) {
      sessionCheckTimeout = setInterval(
        verifySession,
        SESSION_CONFIG.CHECK_INTERVAL,
      );
    }

    return () => {
      if (sessionCheckTimeout) {
        clearInterval(sessionCheckTimeout);
      }
    };
  }, [state.authState, state.hasAccount, verifySession]); // Adicionada dependência verifySession

  // Efeito para gerenciar conexão do socket
  useEffect(() => {
    if (
      state.account &&
      !socket.current?.connected &&
      !state.socketState.isConnecting
    ) {
      initializeSocket(state.account);
    }
  }, [state.account, state.socketState.isConnecting, initializeSocket]); // Adicionada dependência initializeSocket

  // Efeito para limpar timeouts quando o componente for desmontado
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
        redirectTimeoutRef.current = null;
      }
      if (tokenRefreshTimeoutRef.current) {
        clearTimeout(tokenRefreshTimeoutRef.current);
        tokenRefreshTimeoutRef.current = null;
      }
      // Limpar socket e handlers
      socketService.removeEventHandlers();
      socketService.disconnect();
    };
  }, []);

  return (
    <AccountContext.Provider
      value={{
        account: state.account,
        setAccount: handleSetAccount,
        login: async (identifier: string, password: string) => {
          try {
            updateState({ isLoading: true, error: null });
            const account = await authService.login(identifier, password);
            updateState({
              account,
              hasAccount: true,
              authState: "login",
              session: {
                lastCheck: Date.now(),
                lockExpiration: Date.now() + SESSION_CONFIG.LOCK_TIMEOUT,
              },
            });

            // Iniciar refresh automático de token
            scheduleTokenRefresh();

            if (account && !socket.current?.connected) {
              initializeSocket(account);
            }
          } catch (error) {
            handleSessionError(error);
          } finally {
            updateState({ isLoading: false });
          }
        },
        logout: handleLogout,
        socket: { current: socket.current },
        socketState: state.socketState,
        isLoading: state.isLoading,
        error: state.error,
        authState: state.authState,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
};

// Hook personalizado para usar o contexto
export const useAccount = () => {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error("useAccount must be used within an AccountProvider");
  }
  return context;
};
